import { renderArtifactFileCard } from '@/modules/agent-builder/components/renderArtifactFileCard';

import type { IRainAgentConfig } from '../hooks/scope';
import type { TRainToolRenderer } from '../hooks/types';

import { renderAttachFileCard } from './renderers/builder';
import { renderConnectionPickerCard } from './renderers/connection';
import { resolveAutomationToolRenderer } from './renderers/automation';

/**
 * Behavioral config for the agent-builder Sandbox — the "Test your agent" chat
 * that talks to a user-created agent.
 *
 * A user-created agent has no fixed tool set, so no allowlist of tool names can
 * be complete for it, and anything an allowlist omits is swallowed silently.
 * The Sandbox exists to show what the agent actually does, so every call is
 * visible. That leaves `isRunningInternalTool` always false, which is fine:
 * every call renders its own card rather than a generic working indicator.
 *
 * The renderers cover the platform-provided tools whose card is the only way to
 * answer them — they block on client input, so a generic read-only card would
 * leave the agent waiting on something the user cannot reach. Tools this map
 * does not name still render, as generic cards.
 *
 * AUTOMATIONS are the one family that cannot be named here at all, and they are
 * also the one that most needs a card: a run takes real time and can change real
 * records, so the generic card's argument dump is the worst possible treatment.
 * They resolve in two halves, for the reason set out in `automation-tools.ts` —
 * the run-inspection companions match by shape via `resolveToolRenderer`, while
 * the base tools have to be discovered per agent and passed in as
 * `automationRenderers` (see `useAgentAutomationRenderers`).
 */
export const createSandboxAgentConfig = (
  automationRenderers: Readonly<Record<string, TRainToolRenderer>> = {},
): IRainAgentConfig => ({
  // The whole premise of the Sandbox: show what the agent actually did. Nothing
  // is dropped, nothing is demoted to a copy-only row, and nothing folds.
  toolVerbosity: 'full',
  isVisibleTool: () => true,
  toolRenderers: {
    // Discovered from the agent's automations tool source. Spread FIRST so that
    // if a workspace automation's slug ever collides with a platform tool name,
    // the platform card below wins — those block on user input, and losing one
    // would leave the agent waiting on something the user cannot reach.
    ...automationRenderers,
    // Same arguments and resolution path, so both point at the shared picker —
    // as in `BUILDER_AGENT_CONFIG`, which offers the same pair.
    request_connection: renderConnectionPickerCard,
    add_integration: renderConnectionPickerCard,
    attach_file: renderAttachFileCard,
    // A file the agent produced is an OUTCOME of the conversation, not tool
    // trivia — the generic card rendered `artifact_write` as its raw arguments,
    // which for a report means dumping the whole document into the transcript.
    // The Sandbox is the only surface that shows these: every other agent hides
    // the `artifact_*` family (`isRainScaffoldingTool`), and only the playground
    // has the viewer mounted to open one.
    artifact_write: renderArtifactFileCard,
    artifact_edit: renderArtifactFileCard,
  },
  resolveToolRenderer: resolveAutomationToolRenderer,
});

/**
 * The Sandbox config with no automations discovered — the shape used by callers
 * that render a target agent's chat without the tool-catalog queries.
 */
export const SANDBOX_AGENT_CONFIG: IRainAgentConfig =
  createSandboxAgentConfig();
